import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./agenda.style.css";
import Loader from "../Loader/Loader";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";
import { isOwnerOrFinance } from "../../utils/role-check";
import AgendaAssignmentModal from "../Assignments/AgendaAssignmentModal";
import api from "../../Api";

function AgendaView({ projects, assignments, loading, error, auth, onAddAssignment, onDeleteAssignment }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [viewMode, setViewMode] = useState("month"); // month, week, list
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [expandedDay, setExpandedDay] = useState(null);
    const [users, setUsers] = useState([]);
    const [filterUserId, setFilterUserId] = useState("");
    const [filterProjectId, setFilterProjectId] = useState("");
    const [showAssignments, setShowAssignments] = useState(true);

    // Fetch users for assignment modal
    useEffect(() => {
        const fetchUsers = async () => {
            if (isOwnerOrFinance(auth?.role)) {
                try {
                    const response = await api.get("/users");
                    setUsers(response.data || []);
                } catch (err) {
                    console.error("Error fetching users:", err);
                }
            }
        };
        fetchUsers();
    }, [auth]);

    if (loading) return <Loader message={t('common.loading')} />;
    if (error) return <NoDataConstruction text={t('common.error')}/>;

    // Filter projects with dates
    const projectsWithDates = projects.filter(p => p.start_date && p.end_date);

    // Generate consistent color for project based on ID
    const getProjectColor = (projectId) => {
        const colors = [
            '#6B8CAE', // Primary blue
            '#10B981', // Green
            '#F59E0B', // Amber
            '#EF4444', // Red
            '#8B5CF6', // Purple
            '#EC4899', // Pink
            '#14B8A6', // Teal
            '#F97316', // Orange
            '#3B82F6', // Blue
            '#84CC16', // Lime
        ];
        return colors[projectId % colors.length];
    };

    // Get calendar days for current month
    const getCalendarDays = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        // Previous month days
        const prevMonthLastDay = new Date(year, month, 0).getDate();
        for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            days.push({
                date: new Date(year, month - 1, prevMonthLastDay - i),
                isCurrentMonth: false
            });
        }

        // Current month days
        for (let i = 1; i <= daysInMonth; i++) {
            days.push({
                date: new Date(year, month, i),
                isCurrentMonth: true
            });
        }

        // Next month days to complete grid
        const remainingDays = 42 - days.length; // 6 weeks
        for (let i = 1; i <= remainingDays; i++) {
            days.push({
                date: new Date(year, month + 1, i),
                isCurrentMonth: false
            });
        }

        return days;
    };

    // Calculate quarterly financial overview
    const getQuarterlyFinancials = () => {
        const currentYear = new Date().getFullYear();
        const quarters = [
            { name: 'Q1', months: [0, 1, 2] },
            { name: 'Q2', months: [3, 4, 5] },
            { name: 'Q3', months: [6, 7, 8] },
            { name: 'Q4', months: [9, 10, 11] }
        ];

        return quarters.map(quarter => {
            const quarterProjects = projects.filter(project => {
                const endDate = new Date(project.end_date);
                return endDate.getFullYear() === currentYear &&
                       quarter.months.includes(endDate.getMonth());
            });

            const revenue = quarterProjects.reduce((sum, p) => sum + (parseFloat(p.total_price) || 0), 0);
            const costs = quarterProjects.reduce((sum, p) => sum + (parseFloat(p.total_cost) || 0), 0);
            const profit = revenue - costs;

            // Dutch VAT rate is 21%
            const vatAmount = revenue * 0.21;
            const profitAfterVat = profit - vatAmount;

            return {
                ...quarter,
                projectCount: quarterProjects.length,
                revenue,
                costs,
                profit,
                vatAmount,
                profitAfterVat
            };
        });
    };

    // Get projects for a specific date
    const getProjectsForDate = (date) => {
        let filtered = projectsWithDates.filter(project => {
            const startDate = new Date(project.start_date);
            const endDate = new Date(project.end_date);
            const checkDate = new Date(date);

            // Reset time parts for accurate comparison
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            checkDate.setHours(0, 0, 0, 0);

            return checkDate >= startDate && checkDate <= endDate;
        });

        // Apply project filter
        if (filterProjectId) {
            filtered = filtered.filter(p => p.id === parseInt(filterProjectId));
        }

        return filtered;
    };

    // Get assignments for a specific date
    const getAssignmentsForDate = (date) => {
        if (!assignments || assignments.length === 0) return [];

        let filtered = assignments.filter(assignment => {
            const startDate = new Date(assignment.start_date);
            const endDate = new Date(assignment.end_date);
            const checkDate = new Date(date);

            // Reset time parts for accurate comparison
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            checkDate.setHours(0, 0, 0, 0);

            return checkDate >= startDate && checkDate <= endDate;
        });

        // Apply filters
        if (filterUserId) {
            filtered = filtered.filter(a => a.user_id === parseInt(filterUserId));
        }
        if (filterProjectId) {
            filtered = filtered.filter(a => a.project_id === parseInt(filterProjectId));
        }

        return filtered;
    };

    // Calculate financial summary for current period (quarterly)
    const calculateFinancialSummary = () => {
        const VAT_RATE = 0.21; // 21% BTW in Netherlands

        // Determine which quarter the current month falls into
        const currentYear = currentMonth.getFullYear();
        const currentMonthIndex = currentMonth.getMonth(); // 0-11
        const quarter = Math.floor(currentMonthIndex / 3) + 1; // 1-4

        // Calculate quarter start and end dates
        const quarterStartMonth = (quarter - 1) * 3;
        const periodStart = new Date(currentYear, quarterStartMonth, 1);
        const periodEnd = new Date(currentYear, quarterStartMonth + 3, 0); // Last day of quarter

        // Filter projects that are active during this quarter
        const periodProjects = projectsWithDates.filter(project => {
            const projectStart = new Date(project.start_date);
            const projectEnd = new Date(project.end_date);

            // Check if project overlaps with the quarter
            return projectStart <= periodEnd && projectEnd >= periodStart;
        });

        // Apply project filter if set
        const filteredProjects = filterProjectId
            ? periodProjects.filter(p => p.id === parseInt(filterProjectId))
            : periodProjects;

        // Calculate totals
        const totalRevenue = filteredProjects.reduce((sum, project) => sum + (project.projectprice || 0), 0);
        const totalCosts = filteredProjects.reduce((sum, project) => sum + (project.materialcosts || 0), 0);
        const totalProfit = filteredProjects.reduce((sum, project) => sum + (project.profit || 0), 0);

        // VAT is calculated on revenue (excluding VAT)
        const revenueExclVAT = totalRevenue / (1 + VAT_RATE);
        const vatAmount = totalRevenue - revenueExclVAT;

        return {
            projectCount: filteredProjects.length,
            totalRevenue,
            revenueExclVAT,
            vatAmount,
            totalCosts,
            totalProfit,
            periodStart,
            periodEnd,
            quarter,
            year: currentYear
        };
    };

    const financialSummary = calculateFinancialSummary();

    // Calculate all quarters for the current year
    const calculateAllQuarters = () => {
        const VAT_RATE = 0.21;
        const currentYear = currentMonth.getFullYear();
        const quarters = [];

        for (let q = 1; q <= 4; q++) {
            const quarterStartMonth = (q - 1) * 3;
            const periodStart = new Date(currentYear, quarterStartMonth, 1);
            const periodEnd = new Date(currentYear, quarterStartMonth + 3, 0);

            // Filter projects for this quarter
            const quarterProjects = projectsWithDates.filter(project => {
                const projectStart = new Date(project.start_date);
                const projectEnd = new Date(project.end_date);
                return projectStart <= periodEnd && projectEnd >= periodStart;
            });

            // Apply project filter if set
            const filteredProjects = filterProjectId
                ? quarterProjects.filter(p => p.id === parseInt(filterProjectId))
                : quarterProjects;

            // Calculate totals
            const totalRevenue = filteredProjects.reduce((sum, project) => sum + (project.projectprice || 0), 0);
            const totalCosts = filteredProjects.reduce((sum, project) => sum + (project.materialcosts || 0), 0);
            const totalProfit = filteredProjects.reduce((sum, project) => sum + (project.profit || 0), 0);
            const revenueExclVAT = totalRevenue / (1 + VAT_RATE);
            const vatAmount = totalRevenue - revenueExclVAT;

            quarters.push({
                quarter: q,
                periodStart,
                periodEnd,
                projectCount: filteredProjects.length,
                totalRevenue,
                revenueExclVAT,
                vatAmount,
                totalCosts,
                totalProfit,
                isCurrentQuarter: q === financialSummary.quarter
            });
        }

        // Calculate year totals
        const yearTotal = {
            projectCount: quarters.reduce((sum, q) => sum + q.projectCount, 0),
            totalRevenue: quarters.reduce((sum, q) => sum + q.totalRevenue, 0),
            revenueExclVAT: quarters.reduce((sum, q) => sum + q.revenueExclVAT, 0),
            vatAmount: quarters.reduce((sum, q) => sum + q.vatAmount, 0),
            totalCosts: quarters.reduce((sum, q) => sum + q.totalCosts, 0),
            totalProfit: quarters.reduce((sum, q) => sum + q.totalProfit, 0),
        };

        return { quarters, yearTotal, currentYear };
    };

    const quarterlyData = calculateAllQuarters();

    // Navigate months
    const goToPreviousMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    };

    const goToNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    };

    const goToToday = () => {
        setCurrentMonth(new Date());
    };

    // Format date helpers
    const monthYearFormat = currentMonth.toLocaleDateString("nl-NL", { month: "long", year: "numeric" });

    // Get unique users assigned to a specific project
    const getAssignedUsersForProject = (projectId) => {
        if (!projectId || !assignments || assignments.length === 0) {
            return [];
        }

        const projectAssignments = assignments.filter(a => a.project_id === parseInt(projectId));
        const userIds = [...new Set(projectAssignments.map(a => a.user_id))];

        return users.filter(user => userIds.includes(user.id));
    };

    // Get filtered users based on project selection
    const getFilteredUsers = () => {
        if (filterProjectId) {
            // Only show users assigned to the selected project
            return getAssignedUsersForProject(filterProjectId);
        }
        // Show all zzper and voorman users
        return users.filter(u => u.role === 'zzper' || u.role === 'voorman');
    };

    // Reset user filter when project filter changes
    const handleProjectFilterChange = (projectId) => {
        setFilterProjectId(projectId);

        // If a project is selected and current user filter is not in the assigned users, reset it
        if (projectId && filterUserId) {
            const assignedUsers = getAssignedUsersForProject(projectId);
            const isUserAssigned = assignedUsers.some(u => u.id === parseInt(filterUserId));
            if (!isUserAssigned) {
                setFilterUserId("");
            }
        }
    };

    const calendarDays = getCalendarDays();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const quarterlyFinancials = getQuarterlyFinancials();
    const filteredUsers = getFilteredUsers();

    return (
        <div className="agenda-container">
            <div className="agenda-header">
                <h1>{t('agenda.title')}</h1>
                <p className="agenda-subtitle">{t('agenda.planningOverview')}</p>
            </div>

            {/* Controls */}
            <div className="agenda-controls">
                <div className="agenda-navigation">
                    <button onClick={goToPreviousMonth} className="btn-nav">← {t('common.previous')}</button>
                    <button onClick={goToToday} className="btn-today">{t('agenda.today')}</button>
                    <button onClick={goToNextMonth} className="btn-nav">{t('common.next')} →</button>
                </div>
                <h2 className="agenda-month-title">{monthYearFormat}</h2>
                <div className="agenda-view-toggle">
                    <button
                        className={`btn-view-mode ${viewMode === "month" ? "active" : ""}`}
                        onClick={() => setViewMode("month")}
                        title={t('agenda.month')}
                    >
                        <span className="view-mode-icon">📅</span>
                        <span className="view-mode-text">{t('agenda.month')}</span>
                    </button>
                    <button
                        className={`btn-view-mode ${viewMode === "list" ? "active" : ""}`}
                        onClick={() => setViewMode("list")}
                        title={t('agenda.list')}
                    >
                        <span className="view-mode-icon">📋</span>
                        <span className="view-mode-text">{t('agenda.list')}</span>
                    </button>
                    {isOwnerOrFinance(auth?.role) && (
                        <button
                            className="btn-add-timesheet"
                            onClick={() => {
                                setShowAssignmentModal(true);
                            }}
                            title={t('agenda.assignEmployee')}
                        >
                            + {t('agenda.assign')}
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="agenda-filters">
                <div className="filter-group">
                    <label htmlFor="filter-project">{t('agenda.filterByProject')}:</label>
                    <select
                        id="filter-project"
                        value={filterProjectId}
                        onChange={(e) => handleProjectFilterChange(e.target.value)}
                        className="filter-select"
                    >
                        <option value="">{t('agenda.allProjects')}</option>
                        {projects.map(project => (
                            <option key={project.id} value={project.id}>
                                {project.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-user">{t('agenda.filterByEmployee')}:</label>
                    <select
                        id="filter-user"
                        value={filterUserId}
                        onChange={(e) => setFilterUserId(e.target.value)}
                        className="filter-select"
                        disabled={filterProjectId && filteredUsers.length === 0}
                    >
                        <option value="">
                            {filterProjectId
                                ? (filteredUsers.length > 0 ? t('agenda.allAssignedEmployees') : t('agenda.noAssignedEmployees'))
                                : t('agenda.allEmployees')
                            }
                        </option>
                        {filteredUsers.map(user => (
                            <option key={user.id} value={user.id}>
                                {user.name} ({user.role === 'zzper' ? 'ZZP' : t('user.roles.voorman')})
                            </option>
                        ))}
                    </select>
                    {filterProjectId && filteredUsers.length === 0 && (
                        <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                            {t('agenda.noEmployeesAssignedToProject')}
                        </small>
                    )}
                </div>

                <div className="filter-group">
                    <label className="filter-checkbox">
                        <input
                            type="checkbox"
                            checked={showAssignments}
                            onChange={(e) => setShowAssignments(e.target.checked)}
                        />
                        <span>{t('agenda.showAssignments')}</span>
                    </label>
                </div>

                {(filterUserId || filterProjectId) && (
                    <button
                        className="btn-clear-filters"
                        onClick={() => {
                            setFilterUserId("");
                            setFilterProjectId("");
                        }}
                    >
                        ✕ {t('agenda.clearFilters')}
                    </button>
                )}
            </div>

            {projectsWithDates.length === 0 ? (
                <NoDataConstruction text={t('agenda.noProjectsWithPlanning')} />
            ) : viewMode === "month" ? (
                <>
                    {/* Calendar Header */}
                    <div className="calendar-header">
                        <div className="calendar-day-name">{t('agenda.days.sun')}</div>
                        <div className="calendar-day-name">{t('agenda.days.mon')}</div>
                        <div className="calendar-day-name">{t('agenda.days.tue')}</div>
                        <div className="calendar-day-name">{t('agenda.days.wed')}</div>
                        <div className="calendar-day-name">{t('agenda.days.thu')}</div>
                        <div className="calendar-day-name">{t('agenda.days.fri')}</div>
                        <div className="calendar-day-name">{t('agenda.days.sat')}</div>
                    </div>

                    {/* Calendar Grid */}
                    <div className="calendar-grid">
                        {calendarDays.map((day, index) => {
                            const dayProjects = getProjectsForDate(day.date);
                            const dayAssignments = getAssignmentsForDate(day.date);
                            const isToday = day.date.getTime() === today.getTime();
                            const isExpanded = expandedDay === index;

                            // Smart display limits - show more when not many items
                            const totalItems = dayProjects.length + (showAssignments ? dayAssignments.length : 0);
                            const projectDisplayLimit = isExpanded ? dayProjects.length : Math.min(2, dayProjects.length);
                            const assignmentDisplayLimit = isExpanded ? dayAssignments.length : Math.min(3, dayAssignments.length);

                            const hasMoreProjects = dayProjects.length > projectDisplayLimit;
                            const hasMoreAssignments = showAssignments && dayAssignments.length > assignmentDisplayLimit;

                            return (
                                <div
                                    key={index}
                                    className={`calendar-day ${!day.isCurrentMonth ? "other-month" : ""} ${isToday ? "today" : ""} ${isExpanded ? "expanded" : ""}`}
                                    onClick={() => {
                                        if (!isExpanded && totalItems > (projectDisplayLimit + assignmentDisplayLimit)) {
                                            setExpandedDay(index);
                                        }
                                    }}
                                >
                                    <div className="calendar-day-number">
                                        {day.date.getDate()}
                                        {totalItems > 0 && !isExpanded && (
                                            <span className="calendar-day-count" title={`${totalItems} item${totalItems > 1 ? 's' : ''}`}>
                                                {totalItems}
                                            </span>
                                        )}
                                    </div>
                                    <div className="calendar-day-content">
                                        {/* Project badges */}
                                        {dayProjects.length > 0 && (
                                            <div className="calendar-day-projects">
                                                {dayProjects.slice(0, projectDisplayLimit).map(project => (
                                                    <div
                                                        key={project.id}
                                                        className="calendar-project-badge"
                                                        style={{ backgroundColor: getProjectColor(project.id) }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/projects/${project.id}`);
                                                        }}
                                                        title={`${project.name} - ${project.client}\nKlik om project te bekijken`}
                                                    >
                                                        <span className="badge-icon">📋</span>
                                                        <span className="badge-text">{project.name}</span>
                                                    </div>
                                                ))}
                                                {hasMoreProjects && !isExpanded && (
                                                    <div
                                                        className="calendar-more-indicator"
                                                        title={`${dayProjects.length - projectDisplayLimit} ${t('agenda.moreProjects')}`}
                                                    >
                                                        +{dayProjects.length - projectDisplayLimit} {t('agenda.more')}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Assignment badges */}
                                        {showAssignments && dayAssignments.length > 0 && (
                                            <div className="calendar-day-assignments">
                                                {dayAssignments.slice(0, assignmentDisplayLimit).map(assignment => {
                                                    const project = projects.find(p => p.id === assignment.project_id);
                                                    return (
                                                        <div
                                                            key={assignment.id}
                                                            className="calendar-assignment-badge"
                                                            style={{ borderLeftColor: getProjectColor(assignment.project_id) }}
                                                            title={`${assignment.user_name} ${t('agenda.on')} ${project?.name || t('projects.title')}`}
                                                        >
                                                            <span className="assignment-icon">👤</span>
                                                            <span className="assignment-user">{assignment.user_name.split(' ')[0]}</span>
                                                            {isExpanded && project && (
                                                                <span className="assignment-project-name">→ {project.name}</span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                {hasMoreAssignments && !isExpanded && (
                                                    <div
                                                        className="calendar-more-indicator"
                                                        title={`${dayAssignments.length - assignmentDisplayLimit} ${t('agenda.moreAssignments')}`}
                                                    >
                                                        +{dayAssignments.length - assignmentDisplayLimit} {t('agenda.more')}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {isExpanded && (
                                            <button
                                                className="calendar-collapse-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedDay(null);
                                                }}
                                            >
                                                ✕ {t('common.close')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                /* List View */
                <div className="agenda-list-view">
                    {projectsWithDates
                        .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
                        .map(project => {
                            const startDate = new Date(project.start_date);
                            const endDate = new Date(project.end_date);
                            const duration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                            const isActive = startDate <= today && endDate >= today;
                            const isPast = endDate < today;
                            const isFuture = startDate > today;

                            return (
                                <div
                                    key={project.id}
                                    className={`agenda-list-item ${isActive ? "active" : ""} ${isPast ? "past" : ""} ${isFuture ? "future" : ""}`}
                                    onClick={() => navigate(`/projects/${project.id}`)}
                                >
                                    <div
                                        className="agenda-list-color-indicator"
                                        style={{ backgroundColor: getProjectColor(project.id) }}
                                        title={project.name}
                                    />
                                    <div className="agenda-list-item-dates">
                                        <div className="agenda-list-date-badge">
                                            <div className="date-day">{startDate.getDate()}</div>
                                            <div className="date-month">{startDate.toLocaleDateString("nl-NL", { month: "short" })}</div>
                                        </div>
                                        <div className="agenda-list-arrow">→</div>
                                        <div className="agenda-list-date-badge">
                                            <div className="date-day">{endDate.getDate()}</div>
                                            <div className="date-month">{endDate.toLocaleDateString("nl-NL", { month: "short" })}</div>
                                        </div>
                                    </div>
                                    <div className="agenda-list-item-content">
                                        <h3 className="agenda-list-item-title">{project.name}</h3>
                                        <div className="agenda-list-item-details">
                                            <span className="detail-item">📍 {project.location}</span>
                                            <span className="detail-item">👤 {project.client}</span>
                                            <span className="detail-item">📅 {duration} {duration === 1 ? t('agenda.day') : t('agenda.day_plural')}</span>
                                        </div>
                                        {isOwnerOrFinance(auth?.role) && typeof project.profit === 'number' && (
                                            <div className="agenda-list-item-profit">
                                                <span className={`profit-badge ${project.profit >= 0 ? "positive" : "negative"}`}>
                                                    {project.profit >= 0 ? t('projects.profit') : t('projects.loss')}: € {Math.abs(project.profit).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="agenda-list-item-status">
                                        {isPast && <span className="status-badge past-badge">{t('agenda.completed')}</span>}
                                        {isActive && <span className="status-badge active-badge">{t('agenda.active')}</span>}
                                        {isFuture && <span className="status-badge future-badge">{t('agenda.planned')}</span>}
                                    </div>
                                </div>
                            );
                        })}
                </div>
            )}

            {/* Assignment Modal */}
            <AgendaAssignmentModal
                isOpen={showAssignmentModal}
                onClose={() => {
                    setShowAssignmentModal(false);
                    setSelectedProject(null);
                    setSelectedDate(null);
                }}
                onSave={async (assignmentData) => {
                    const success = await onAddAssignment(assignmentData);
                    if (success) {
                        setShowAssignmentModal(false);
                        setSelectedProject(null);
                        setSelectedDate(null);
                    }
                }}
                projects={projects}
                initialProjectId={selectedProject?.id}
                initialDate={selectedDate}
            />
        </div>
    );
}

AgendaView.propTypes = {
    projects: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    auth: PropTypes.object
};

export default AgendaView;

